import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'wouter';
import { ArrowLeft, BarChart3, ExternalLink, Flag, MapPin, Pencil, Pin, PinOff, Plus, Settings, Trash2, Youtube } from 'lucide-react';
import type { ClubAPI, PollAPI } from '@shared/types/api';
import { useAdminClub, useDismissClubReport, useRemoveClubPost, useUpdateAdminClubPost } from '../api/useAdminClubs';
import { useToast } from '@/components/ux/Toast';
import { useConfirm } from '@/components/ConfirmModal';
import { Loader } from '@/components/Loader';
import { ClubPostDialog } from '../components/clubs/ClubPostDialog';
import { ClubPollDialog } from '../components/clubs/ClubPollDialog';
import { ClubSettingsDialog } from '../components/clubs/ClubSettingsDialog';

const APP_ORIGIN = 'https://app.outclose.com';

type Tab = 'overview' | 'posts' | 'polls';

/** Which dialog is open; `item` set means editing. */
type DialogState =
  | { kind: 'post'; item?: ClubAPI.ClubPost }
  | { kind: 'poll'; item?: PollAPI.Poll }
  | { kind: 'settings' }
  | null;

const STATUS_CLASS: Record<string, string> = {
  active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  draft: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400',
  unopened: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
  closed: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  archived: 'bg-neutral-100 text-neutral-400 dark:bg-neutral-800 dark:text-neutral-500',
};

function StatusBadge({ status }: { status: string }) {
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CLASS[status] ?? STATUS_CLASS.draft}`}>{status}</span>;
}

/** Title for list rows; posts from before titles existed fall back to their first line. */
function postTitle(post: ClubAPI.ClubPost) {
  if (post.title) return post.title;
  const firstLine = post.body.split('\n').map((l) => l.trim()).find(Boolean) ?? '';
  return firstLine.replace(/^#{1,6}\s+/, '').replace(/[*_`~]/g, '').slice(0, 120) || '(untitled)';
}

const formatDate = (value: string | Date) =>
  new Date(value).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

function SectionHeader({ title, action }: { title: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-lg font-semibold dark:text-neutral-100">{title}</h2>
      {action}
    </div>
  );
}

function PrimaryButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors"
    >
      {children}
    </button>
  );
}

const iconButton = 'p-1.5 rounded-md text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800';

export default function AdminClubDetail() {
  const { clubId } = useParams<{ clubId: string }>();
  const toast = useToast();
  const { confirm } = useConfirm();
  const { data, isLoading, error } = useAdminClub(clubId);
  const updatePost = useUpdateAdminClubPost();
  const removePost = useRemoveClubPost();
  const dismissReport = useDismissClubReport();
  const [tab, setTab] = useState<Tab>('overview');
  const [dialog, setDialog] = useState<DialogState>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader />
      </div>
    );
  }
  if (error || !data || !clubId) {
    return <div className="p-6 text-red-600">Club not found.</div>;
  }

  const { club } = data;
  const onError = (err: any) => toast.error(err?.message || 'Something went wrong');
  const readyCities = data.cities.filter((c) => c.ready).length;

  const createEventUrl = (cityId: string, cityName: string | null) => {
    const params = new URLSearchParams({ club: club.id, city: cityId });
    if (cityName) params.set('cityName', cityName);
    return `${APP_ORIGIN}/events/new?${params}`;
  };

  const handleRemovePost = async (post: ClubAPI.ClubPost) => {
    const ok = await confirm({
      title: 'Remove post',
      message: `Remove "${postTitle(post)}" from the forum?`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      type: 'danger',
    });
    if (ok) removePost.mutate({ clubId: club.id, postId: post.id }, { onError });
  };

  const tabs: { key: Tab; label: string; count?: number; alert?: boolean }[] = [
    { key: 'overview', label: 'Overview', alert: readyCities > 0 || data.reports.length > 0 },
    { key: 'posts', label: 'Posts', count: data.posts.length },
    { key: 'polls', label: 'Polls', count: data.polls.length },
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="p-6 max-w-5xl">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <Link href="/clubs" className="text-neutral-500 hover:text-primary-500">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          {club.imageUrl && <img src={club.imageUrl} alt="" className="w-10 h-10 rounded-lg object-contain bg-neutral-100 dark:bg-neutral-800" />}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold dark:text-neutral-100 truncate">{club.name}</h1>
              <StatusBadge status={club.status} />
            </div>
            <div className="text-sm text-neutral-500">
              {club.audience} · {data.memberCount} members
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`${APP_ORIGIN}/clubs/${club.slug}?ff=clubs_tab:experiment`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 px-3 py-2 text-sm rounded-lg border border-neutral-200 dark:border-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800"
          >
            View in app <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <button
            onClick={() => setDialog({ kind: 'settings' })}
            className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-neutral-200 dark:border-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800"
          >
            <Settings className="w-4 h-4" /> Settings
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-neutral-200 dark:border-neutral-700 mb-6">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${
              tab === t.key ? 'text-primary-500' : 'text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200'
            }`}
          >
            {t.label}
            {t.count != null && <span className="ml-1.5 text-xs text-neutral-400">{t.count}</span>}
            {t.alert && <span className="absolute top-2 right-1.5 w-1.5 h-1.5 rounded-full bg-red-500" />}
            {tab === t.key && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-primary-500" />}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab === 'overview' && (
        <div className="space-y-8">
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Members', value: data.memberCount },
              { label: 'Cities ready for an event', value: readyCities, highlight: readyCities > 0 },
              { label: 'Open reports', value: data.reports.length, highlight: data.reports.length > 0 },
            ].map((s) => (
              <div key={s.label} className="card p-4">
                <div className={`text-2xl font-bold ${s.highlight ? 'text-amber-600' : 'dark:text-neutral-100'}`}>{s.value}</div>
                <div className="text-xs text-neutral-500">{s.label}</div>
              </div>
            ))}
          </div>

          <section>
            <SectionHeader title="Members by city" />
            <div className="card p-0 overflow-hidden">
              {data.cities.length === 0 ? (
                <p className="p-5 text-sm text-neutral-500">No members with a city yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {data.cities.map((c) => (
                      <tr key={c.cityId} className="border-b last:border-0 border-neutral-100 dark:border-neutral-800">
                        <td className="py-2.5 px-4 font-medium dark:text-neutral-100">
                          <span className="inline-flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5 text-neutral-400" /> {c.cityName ?? c.cityId}
                          </span>
                        </td>
                        <td className="py-2.5 px-4 dark:text-neutral-200">
                          {c.members} / {c.threshold}
                        </td>
                        <td className="py-2.5 px-4 text-right">
                          {c.hasUpcomingEvent ? (
                            <span className="text-green-600">Event scheduled</span>
                          ) : c.ready ? (
                            <a
                              href={createEventUrl(c.cityId, c.cityName)}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-amber-500 text-white text-xs font-medium hover:bg-amber-600"
                            >
                              Ready — create event <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : (
                            <span className="text-neutral-500">{c.threshold - c.members} to go</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>

          {data.reports.length > 0 && (
            <section>
              <SectionHeader title="Reported posts" />
              <div className="card p-0 divide-y divide-neutral-100 dark:divide-neutral-800">
                {data.reports.map((r) => (
                  <div key={r.id} className="flex items-start justify-between gap-4 p-4 text-sm">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 text-xs text-red-600 font-medium mb-1">
                        <Flag className="w-3.5 h-3.5" /> {r.reason}
                        {r.description ? ` — ${r.description}` : ''}
                      </div>
                      <div className="line-clamp-3 dark:text-neutral-200">{r.postBody}</div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        className="px-3 py-1 rounded-lg bg-red-500 text-white text-xs"
                        onClick={() => removePost.mutate({ clubId: club.id, postId: r.postId }, { onError })}
                      >
                        Remove post
                      </button>
                      <button
                        className="px-3 py-1 rounded-lg border text-xs dark:border-neutral-700 dark:text-neutral-200"
                        onClick={() => dismissReport.mutate({ clubId: club.id, reportId: r.id }, { onError })}
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {/* Posts */}
      {tab === 'posts' && (
        <section>
          <SectionHeader
            title="Forum threads"
            action={
              <PrimaryButton onClick={() => setDialog({ kind: 'post' })}>
                <Plus className="w-4 h-4" /> New post
              </PrimaryButton>
            }
          />
          <div className="card p-0 overflow-hidden">
            {data.posts.length === 0 ? (
              <p className="p-5 text-sm text-neutral-500">No posts yet.</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {data.posts.map((p) => (
                    <tr key={p.id} className="border-b last:border-0 border-neutral-100 dark:border-neutral-800">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {p.pinnedAt && <Pin className="w-3.5 h-3.5 text-primary-500 flex-shrink-0" />}
                          {p.youtubeVideoId && <Youtube className="w-3.5 h-3.5 text-red-500 flex-shrink-0" aria-label="Has video" />}
                          <span className="font-medium dark:text-neutral-100">{postTitle(p)}</span>
                        </div>
                        <div className="text-xs text-neutral-500">
                          {p.isAdminPost ? 'Outclose Team' : p.author?.firstName ?? 'Former member'} · {formatDate(p.createdAt)}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex justify-end gap-1">
                          {p.isAdminPost && (
                            <button title="Edit" onClick={() => setDialog({ kind: 'post', item: p })} className={iconButton}>
                              <Pencil className="w-4 h-4" />
                            </button>
                          )}
                          {p.isAdminPost && (
                            <button
                              title={p.pinnedAt ? 'Unpin from club page' : 'Pin to club page'}
                              onClick={() => updatePost.mutate({ clubId: club.id, postId: p.id, data: { pinned: !p.pinnedAt } }, { onError })}
                              className={iconButton}
                            >
                              {p.pinnedAt ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                            </button>
                          )}
                          <button title="Remove" onClick={() => handleRemovePost(p)} className={`${iconButton} hover:text-red-500`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      )}

      {/* Polls */}
      {tab === 'polls' && (
        <section>
          <SectionHeader
            title="Club polls"
            action={
              <PrimaryButton onClick={() => setDialog({ kind: 'poll' })}>
                <Plus className="w-4 h-4" /> New poll
              </PrimaryButton>
            }
          />
          <div className="card p-0 overflow-hidden">
            {data.polls.length === 0 ? (
              <p className="p-5 text-sm text-neutral-500">No polls yet.</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {data.polls.map((p) => (
                    <tr key={p.id} className="border-b last:border-0 border-neutral-100 dark:border-neutral-800">
                      <td className="py-3 px-4">
                        <div className="font-medium dark:text-neutral-100">{p.title}</div>
                        <div className="text-xs text-neutral-500">
                          {p.answers.length} options{p.endsAt ? ` · closes ${formatDate(p.endsAt)}` : ''}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <StatusBadge status={p.status} />
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex justify-end gap-1">
                          <button title="Edit" onClick={() => setDialog({ kind: 'poll', item: p })} className={iconButton}>
                            <Pencil className="w-4 h-4" />
                          </button>
                          <Link href={`/polls/${p.id}`} title="Results" className={iconButton}>
                            <BarChart3 className="w-4 h-4" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      )}

      {dialog?.kind === 'post' && <ClubPostDialog clubId={club.id} post={dialog.item} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'poll' && <ClubPollDialog clubId={club.id} poll={dialog.item} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'settings' && <ClubSettingsDialog club={club} onClose={() => setDialog(null)} />}
    </motion.div>
  );
}
